import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function ResourcePage(): Node {
  return html`
    <div>
      <h1>Resource</h1>
      <p><code>createResource</code> provides reactive async data fetching. It tracks loading state, errors, and data — and automatically refetches when the source signal changes.</p>

      <h2>Basic Usage</h2>
      ${CodeBlock(`import { createSignal, createResource } from 'onefold';

const userId = createSignal(1);

const user = createResource(userId, async (id) => {
  const res = await fetch(\`/api/users/\${id}\`);
  return res.json();
});

// Reactive accessors
user.data()     // T | undefined
user.loading()  // boolean
user.error()    // Error | null`)}

      <h2>In Templates</h2>
      ${CodeBlock(`function UserCard(): Node {
  const userId = createSignal(1);
  const user = createResource(userId, async (id) => {
    const res = await fetch(\`/api/users/\${id}\`);
    return res.json();
  });

  return html\`
    <div>
      \${() => user.loading()
        ? html\`<p>Loading...</p>\`
        : user.error()
          ? html\`<p class="error">\${user.error()!.message}</p>\`
          : html\`<p>\${user.data()?.name}</p>\`
      }
      <button onclick=\${() => userId.set(n => n + 1)}>Next User</button>
    </div>
  \`;
}`)}

      <h2>Manual Refetch</h2>
      ${CodeBlock(`// Refetch with the current source value
user.refetch();`)}

      <h2>Cleanup</h2>
      ${CodeBlock(`// Stop watching the source signal
user.dispose();`)}

      ${Callout('When the source signal changes, any in-flight request from the previous source value is ignored (its result will not update .data()).')}

      <h2>Without a Source Signal</h2>
      <p>Pass <code>null</code> as the source to fetch once on creation:</p>
      ${CodeBlock(`const posts = createResource(null, async () => {
  const res = await fetch('/api/posts');
  return res.json();
});

// Fetch again manually
posts.refetch();`)}

      <h2>API Reference</h2>
      <table>
        <tr><th>Property/Method</th><th>Type</th><th>Description</th></tr>
        <tr><td><code>.data()</code></td><td>T | undefined</td><td>The resolved data (reactive).</td></tr>
        <tr><td><code>.loading()</code></td><td>boolean</td><td>True while fetching (reactive).</td></tr>
        <tr><td><code>.error()</code></td><td>Error | null</td><td>The rejection error, if any (reactive).</td></tr>
        <tr><td><code>.refetch()</code></td><td>void</td><td>Re-run the fetcher with current source.</td></tr>
        <tr><td><code>.dispose()</code></td><td>void</td><td>Stop watching the source signal.</td></tr>
      </table>

      <h2>Try It</h2>
      <p>Click "Next User" to change the source signal — <code>createResource</code> auto-refetches:</p>

      ${Playground(`import { createSignal, createResource, html, mount } from 'onefold';

function App() {
  const userId = createSignal(1);

  const user = createResource(userId, async (id) => {
    const res = await fetch('https://jsonplaceholder.typicode.com/users/' + id);
    if (!res.ok) throw new Error('User not found');
    return res.json();
  });

  return html\`
    <div>
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:16px">
        <h3>User Profile</h3>
        <span style="font-size:12px;color:#666">ID: \${() => userId()}</span>
      </div>

      \${() => {
        if (user.loading()) return html\`<p style="color:#666">Loading user \${userId()}...</p>\`;
        if (user.error()) return html\`<p style="color:#ef4444">Error: \${user.error().message}</p>\`;
        const data = user.data();
        if (!data) return null;
        return html\`
          <div style="padding:16px;border:1px solid #e5e7eb;border-radius:8px">
            <div style="display:flex;align-items:center;gap:12px;margin-bottom:12px">
              <div style="width:48px;height:48px;border-radius:50%;background:#4f46e5;color:white;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700">
                \${data.name.charAt(0)}
              </div>
              <div>
                <div style="font-weight:600;font-size:16px">\${data.name}</div>
                <div style="font-size:13px;color:#64748b">\${data.email}</div>
              </div>
            </div>
            <div style="font-size:13px;color:#666">
              <p>Company: \${data.company.name}</p>
              <p>City: \${data.address.city}</p>
              <p>Phone: \${data.phone}</p>
            </div>
          </div>
        \`;
      }}

      <div style="display:flex;gap:8px;margin-top:16px">
        <button onclick=\${() => userId.set(n => Math.max(1, n - 1))}>← Previous</button>
        <button onclick=\${() => userId.set(n => Math.min(10, n + 1))}>Next →</button>
        <button onclick=\${() => user.refetch()} style="margin-left:auto;font-size:12px">Refetch</button>
      </div>
      <p style="font-size:11px;color:#94a3b8;margin-top:8px">Source signal changes → automatic refetch. No manual wiring needed.</p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'createResource — Reactive Data Fetching', { allowNetwork: true })}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/data/http-client">HTTP Client</a> — typed HTTP client with interceptors</li>
        <li><a href="/async/suspense">Suspense</a> — show fallback UI while data loads</li>
      </ul>
    </div>
  `;
}
